/**
 * resource-handler.ts — handles resource_request commands from the frontend.
 *
 * Dispatches list/read/write/delete/search operations to the ConnectorManager.
 * Tool-face connectors are served through the manager's CRUD dispatch;
 * filesystem-face (mountable) connectors are served directly from their
 * projected mount directory. Emits resource_response events back through the
 * transport.
 */

import { mkdir, readdir, readFile, rm, stat, unlink, writeFile } from "node:fs/promises";
import { basename, dirname, isAbsolute, join, relative, resolve, sep } from "node:path";
import type { AuthoritativeSizeProvider, ConnectorManager } from "@skaile/workspaces/connectors";
import { FsDeadlineError, fsDeadlineMs, withFsDeadline } from "@skaile/workspaces/core";
import type {
  AgentEvent,
  ConnectorContent,
  ConnectorRequestCommand,
  ListOptions,
} from "@skaile/workspaces/types";
import mime from "mime";

// ---------------------------------------------------------------------------
// Connector resource handler
// ---------------------------------------------------------------------------

/**
 * Handle a resource_request command by dispatching to the ConnectorManager
 * and emitting a resource_response event.
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export async function handleResourceRequest(
  command: ConnectorRequestCommand,
  manager: ConnectorManager,
  emit: (event: AgentEvent) => void,
): Promise<void> {
  const { requestId, connectorId, operation, path, options, content } = command;

  try {
    let data: unknown;
    switch (operation) {
      case "list":
        data = await manager.list(
          connectorId,
          path,
          options as import("@skaile/workspaces/connectors").ListOptions | undefined,
        );
        break;
      case "read":
        data = await manager.read(connectorId, path!);
        break;
      case "write":
        await manager.write(connectorId, path!, content!);
        data = true;
        break;
      case "delete":
        data = await manager.delete(connectorId, path!);
        break;
      case "search":
        data = await manager.search(
          connectorId,
          path!,
          options as import("@skaile/workspaces/connectors").SearchOptions | undefined,
        );
        break;
    }
    emit({ type: "resource_response", requestId, resourceId: connectorId, operation, data });
  } catch (err) {
    emit({
      type: "resource_response",
      requestId,
      resourceId: connectorId,
      operation,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

// ---------------------------------------------------------------------------
// Mountable-connector resource handler — serves list/read/write from the
// projected mount directory of a filesystem-face connector.
//
// Every call here lands on `handle.mountPath`, which in the agent container IS
// the rclone FUSE mount. Synchronous `fs` on a wedged mount parks the whole
// event loop, so this module uses `node:fs/promises` exclusively and bounds
// each operation with `withFsDeadline`.
// ---------------------------------------------------------------------------

/**
 * Maximum directory depth a recursive mount listing descends.
 *
 * Guards against a symlink/junction cycle on a remote mount turning one folder
 * open into an unbounded walk.
 */
export const MOUNT_LIST_MAX_DEPTH = 32;

/**
 * Maximum number of entries a single mount listing returns.
 *
 * A recursive walk issues one round-trip per directory and one per file on a
 * cloud mount, so an unbounded listing is an unbounded request.
 */
export const MOUNT_LIST_MAX_ENTRIES = 10_000;

/**
 * Deadline multiplier for the recursive delete.
 *
 * The single-op deadline exists to catch a *wedged* mount, where nothing
 * returns. A recursive `rm` on a healthy mount is not that: rclone issues one
 * remote DELETE per object with no write-back cache to hide the latency, so a
 * few hundred files legitimately outlast a single `stat`. Charging that the
 * single-op deadline would report "delete failed" while the uncancellable `rm`
 * carries on and the folder does disappear — a wrong error with a real side
 * effect. Matches the tree-op factor used by the catalog deployer and the
 * workspace-config tree copy.
 */
export const MOUNT_TREE_OP_DEADLINE_FACTOR = 4;

/**
 * Deadline multiplier for reading one whole file off the mount.
 *
 * A cold rclone VFS serves the first read of a large file straight from the
 * remote, so honest duration scales with file size, not with mount health. The
 * write path stays on the single-op default deliberately: writes land in the
 * VFS write-back cache and return locally, so a slow write really does mean a
 * sick mount.
 */
export const MOUNT_READ_DEADLINE_FACTOR = 4;

/** Resolve MIME type for a filename. Falls back to application/octet-stream. */
function getMimeType(filename: string): string {
  return mime.getType(filename) ?? "application/octet-stream";
}

/**
 * Returns true if the buffer should be served as base64-encoded binary.
 *
 * Uses a byte-level heuristic: checks the first 8KB for null bytes. This
 * correctly handles all code/config formats regardless of MIME type.
 */
function isBinaryBuffer(buf: Buffer): boolean {
  const limit = Math.min(buf.length, 8192);
  for (let i = 0; i < limit; i++) {
    if (buf[i] === 0) return true;
  }
  return false;
}

/**
 * Length of the run of `0x00` bytes a text buffer ends with.
 *
 * A cloud mount served through an rclone VFS cache can hand back a text file
 * whose declared size exceeds the bytes the remote actually serves (SharePoint
 * metadata sizes are known to drift from content length); the unfilled tail
 * of the cache item reads back as zeros. Those zeros are not content. A text
 * file whose real content ends in NUL bytes does not occur in practice, so the
 * caller strips the run before decoding; binary buffers must never go through
 * this (a zip's end-of-central-directory record legitimately ends in zeros).
 */
export function nulTailPaddingLength(buf: Buffer): number {
  let end = buf.length;
  while (end > 0 && buf[end - 1] === 0) end--;
  return buf.length - end;
}

/**
 * Resolve the optional authoritative-size hook a filesystem-face connector may
 * attach to its `handle.state` (rclone-backed mounts wire it to
 * `RcloneProcessManager.statSize`). Returns `undefined` when the mount does not
 * expose one — local mounts, fleet-managed mounts without an in-container rc
 * server, etc. — so the read path skips the extra round-trip for those.
 */
function getAuthoritativeSize(
  state: unknown,
): ((relPath: string) => Promise<number | null>) | undefined {
  const provider = state as Partial<AuthoritativeSizeProvider> | null | undefined;
  return typeof provider?.authoritativeSize === "function"
    ? (relPath) => provider.authoritativeSize!(relPath)
    : undefined;
}

/**
 * Handle a resource request against a filesystem-face (mountable) connector.
 *
 * All filesystem work is async and deadline-bounded, so a wedged mount fails
 * this one request (`resource_response` with `error`) instead of parking the
 * event loop. Listings are additionally capped by {@link MOUNT_LIST_MAX_DEPTH}
 * and {@link MOUNT_LIST_MAX_ENTRIES}; exceeding either is an error, never a
 * silently truncated result.
 *
 * @docLink packages/runner/dev-guide#flow-execution-turn-based-model
 */
export async function handleMountResourceRequest(
  command: ConnectorRequestCommand,
  manager: ConnectorManager,
  emit: (event: AgentEvent) => void,
): Promise<void> {
  const { requestId, connectorId: mountId, operation, path: reqPath, options, content } = command;

  try {
    const entry = manager.get(mountId);
    const mountPath = entry.handle.mountPath;
    if (!mountPath) {
      throw new Error(`Connector "${mountId}" has no projected mount directory`);
    }
    const targetPath = reqPath ? resolve(mountPath, reqPath) : mountPath;

    // Safety: prevent path traversal outside mount. A prefix check would let a
    // sibling sharing a string prefix (mount /data/mount vs /data/mount-secrets)
    // escape, so use a boundary-aware path.relative() check.
    const rel = relative(mountPath, targetPath);
    if (rel === ".." || rel.startsWith(`..${sep}`) || isAbsolute(rel)) {
      throw new Error("Path traversal outside mount");
    }

    let data: unknown;
    switch (operation) {
      case "list": {
        // Honor `options.recursive`: the platform's folder-rename copies a folder
        // by listing it recursively then copying each file. Without recursion the
        // mount would report only top-level entries and nested subfolders would be
        // silently dropped from the copy.
        const listOptions = options as ListOptions | undefined;
        const recursive = Boolean(listOptions?.recursive);
        // Dotfiles are hidden by default (a browsing listing), and the skip is
        // silent — which is exactly wrong for a caller staging a build context,
        // so `includeHidden` lifts it. The entry cap below counts them too.
        const includeHidden = Boolean(listOptions?.includeHidden);
        const collected: Record<string, unknown>[] = [];
        // Truncation is signalled by throwing, not by returning a short list:
        // the platform's copy-then-delete rename would treat a silently partial
        // listing as complete and delete the un-copied remainder.
        const walk = async (absDir: string, relDir: string, depth: number): Promise<void> => {
          if (depth > MOUNT_LIST_MAX_DEPTH) {
            throw new Error(
              `Listing "${relDir}" exceeds the maximum depth of ${MOUNT_LIST_MAX_DEPTH} — ` +
                `narrow the path or list without recursion`,
            );
          }
          const dirents = await withFsDeadline(`readdir ${absDir}`, () =>
            readdir(absDir, { withFileTypes: true }),
          );
          for (const e of dirents) {
            if (!includeHidden && e.name.startsWith(".")) continue;
            if (collected.length >= MOUNT_LIST_MAX_ENTRIES) {
              // The advice has to match what was actually asked for: a flat
              // folder of >10 000 files trips this without recursion ever
              // having been requested.
              throw new Error(
                `Listing exceeds the maximum of ${MOUNT_LIST_MAX_ENTRIES} entries — ` +
                  (recursive ? "narrow the path or list without recursion" : "narrow the path"),
              );
            }
            const entryRel = relDir ? `${relDir}/${e.name}` : e.name;
            const isDir = e.isDirectory();
            const result: Record<string, unknown> = {
              name: e.name,
              path: entryRel,
              type: isDir ? "directory" : "file",
            };
            if (!isDir) {
              // Size stays best-effort (a vanished/unreadable file must not fail
              // the listing) but a *deadline* miss means the mount is wedged, so
              // the remaining stats would only stall the request further.
              try {
                const st = await withFsDeadline(`stat ${join(absDir, e.name)}`, () =>
                  stat(join(absDir, e.name)),
                );
                result.size = st.size;
              } catch (statErr) {
                if (statErr instanceof FsDeadlineError) throw statErr;
              }
            }
            collected.push(result);
            if (isDir && recursive) await walk(join(absDir, e.name), entryRel, depth + 1);
          }
        };
        await walk(targetPath, reqPath ?? "", 0);
        data = collected;
        break;
      }
      case "read": {
        const contentType = getMimeType(basename(targetPath));
        const buf = await withFsDeadline(
          `readFile ${targetPath}`,
          () => readFile(targetPath),
          fsDeadlineMs() * MOUNT_READ_DEADLINE_FACTOR,
        );

        // Defeat the rclone VFS non-truncated stale tail: when a remote file is
        // replaced by a shorter version, rclone can serve `[new bytes][leftover
        // tail]` and report the inflated size, so the on-disk length is not
        // trustworthy. When the mount exposes an authoritative remote size
        // (via operations/stat), gate the served bytes to it. Slicing a Buffer
        // is byte-correct; decoding to utf-8 must happen *after* the slice.
        let bytes = buf;
        const sizeFn = getAuthoritativeSize(entry.handle.state);
        if (sizeFn) {
          const authSize = await sizeFn(reqPath ?? "");
          if (authSize !== null && authSize >= 0 && authSize < buf.length) {
            bytes = buf.subarray(0, authSize);
          }
        }

        // The size gate above cannot see a pad the remote's own metadata
        // reports as content, so classify on the bytes before any trailing NUL
        // run and serve text without it: served as characters, an editor
        // would save the pad back as bytes. Binary content keeps every byte.
        const padding = nulTailPaddingLength(bytes);
        const body = bytes.subarray(0, bytes.length - padding);
        if ((padding > 0 && body.length === 0) || isBinaryBuffer(body)) {
          data = {
            data: bytes.toString("base64"),
            contentType,
            encoding: "binary",
          };
        } else {
          data = {
            data: body.toString("utf-8"),
            contentType,
            encoding: "utf-8",
          };
        }
        break;
      }
      case "write": {
        const parentDir = dirname(targetPath);
        await withFsDeadline(`mkdir ${parentDir}`, () => mkdir(parentDir, { recursive: true }));
        const writeContent =
          typeof content === "string"
            ? content
            : ((content as ConnectorContent | undefined)?.data ?? "");
        const encoding =
          typeof content === "string"
            ? undefined
            : (content as ConnectorContent | undefined)?.encoding;
        await withFsDeadline(`writeFile ${targetPath}`, () =>
          encoding === "binary"
            ? writeFile(targetPath, Buffer.from(writeContent, "base64"))
            : writeFile(targetPath, writeContent, "utf-8"),
        );
        data = true;
        break;
      }
      case "delete": {
        // unlink throws EISDIR on directories; the platform renames folders
        // by copy-then-delete, so the delete op must remove directories too.
        const st = await withFsDeadline(`stat ${targetPath}`, () => stat(targetPath));
        if (st.isDirectory()) {
          await withFsDeadline(
            `rm -r ${targetPath}`,
            () => rm(targetPath, { recursive: true, force: true }),
            fsDeadlineMs() * MOUNT_TREE_OP_DEADLINE_FACTOR,
          );
        } else {
          await withFsDeadline(`unlink ${targetPath}`, () => unlink(targetPath));
        }
        data = true;
        break;
      }
      default:
        throw new Error(`Unsupported operation for mount: ${operation}`);
    }
    emit({ type: "resource_response", requestId, resourceId: mountId, operation, data });
  } catch (err) {
    emit({
      type: "resource_response",
      requestId,
      resourceId: mountId,
      operation,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
